/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { create } from 'zustand';
import type { FormulaDto, FormulaListParams, FormulaSortBy } from '@shared/types';
import { fetchFormulas, fetchFilterOptions, fetchAllMatchingSetIds } from '../services/formularyApi';

export type SearchMode = 'both' | 'ingredient' | 'formula';

interface FormulaListState {
  formulas: FormulaDto[];
  totalCount: number;
  page: number;
  pageSize: 25 | 50 | 100;
  status: 'all' | 'assigned' | 'unassigned';
  dosageForm: string | undefined;
  schedule: string | undefined;
  /** Free-text search (matches formula name and/or ingredient name per searchMode) */
  searchText: string | undefined;
  /** Search scope: both | ingredient | formula */
  searchMode: SearchMode;
  sortBy: FormulaSortBy;
  isLoading: boolean;
  error: string | null;
  filterOptions: { dosageForms: string[]; schedules: (string | null)[]; sourceNames: string[] } | null;
  selectedSetIds: Set<string>;
  expandedSetIds: Set<string>;
  /** True when an ingredient-scope search is active (used to show ingredient chip row) */
  isIngredientSearch: boolean;
  /** Total formulas matching the current search (across all pages) */
  searchResultCount: number;
  /** When set, restricts the displayed list to only these formula set_ids (claim filter) */
  claimedSetIds: string[] | null;

  loadFormulas: (bustCache?: boolean) => Promise<void>;
  loadFilterOptions: () => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: 25 | 50 | 100) => void;
  setStatus: (status: 'all' | 'assigned' | 'unassigned') => void;
  setDosageForm: (v: string | undefined) => void;
  setSchedule: (v: string | undefined) => void;
  setSearchText: (v: string | undefined) => void;
  setSearchMode: (v: SearchMode) => void;
  setSortBy: (v: FormulaSortBy) => void;
  /** Clear all filters, search, and sort to defaults */
  clearFilters: () => void;
  toggleSelect: (setId: string) => void;
  selectAllPage: () => void;
  deselectPage: () => void;
  /** Select all set_ids matching the current filter set across all pages */
  selectAllMatchingResults: () => Promise<void>;
  clearSelection: () => void;
  toggleExpanded: (setId: string) => void;
  setClaimedSetIds: (ids: string[] | null) => void;
}

export const useFormulaListStore = create<FormulaListState>((set, get) => ({
  formulas: [],
  totalCount: 0,
  page: 1,
  pageSize: 50,
  status: 'unassigned',
  dosageForm: undefined,
  schedule: undefined,
  searchText: undefined,
  searchMode: 'both',
  sortBy: 'default',
  isLoading: false,
  error: null,
  filterOptions: null,
  selectedSetIds: new Set(),
  expandedSetIds: new Set(),
  isIngredientSearch: false,
  searchResultCount: 0,
  claimedSetIds: null,

  loadFormulas: async (bustCache?: boolean) => {
    const state = get();
    set({ isLoading: true, error: null });

    try {
      const params: FormulaListParams & { _bust?: number } = {
        page: state.page,
        pageSize: state.pageSize,
        status: state.status,
        dosageForm: state.dosageForm,
        schedule: state.schedule,
        sortBy: state.sortBy,
        search: state.searchText,
        searchMode: state.searchMode,
      };
      if (bustCache) params._bust = Date.now();
      const response = await fetchFormulas(params);
      const hasSearch = !!state.searchText?.trim();
      const ingredientScope = hasSearch && (state.searchMode === 'ingredient' || state.searchMode === 'both');

      // Client-side filter: if a claim is active, show only claimed formulas
      const claimed = state.claimedSetIds;
      const filteredData = claimed
        ? response.data.filter((f) => claimed.includes(f.setId))
        : response.data;

      set({
        formulas: filteredData,
        totalCount: claimed ? filteredData.length : (response.meta?.totalCount ?? 0),
        isLoading: false,
        isIngredientSearch: ingredientScope,
        searchResultCount: hasSearch ? (response.meta?.totalCount ?? 0) : 0,
      });
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
    }
  },

  loadFilterOptions: async () => {
    try {
      const response = await fetchFilterOptions();
      set({ filterOptions: response.data });
    } catch {
      // Silently ignore — filters will just be unavailable
    }
  },

  setPage: (page) => { set({ page }); get().loadFormulas(); },
  setPageSize: (pageSize) => {
    const { page: currentPage, pageSize: currentSize } = get();
    const firstItem = (currentPage - 1) * currentSize + 1;
    const newPage = Math.max(1, Math.ceil(firstItem / pageSize));
    set({ pageSize, page: newPage });
    get().loadFormulas();
  },
  setStatus: (status) => {
    // Auto-switch sort when viewing assigned formulas
    const sortBy = status === 'assigned' ? 'assigned_date_desc' : 'default';
    set({ status, sortBy, page: 1 });
    get().loadFormulas();
  },
  setDosageForm: (v) => { set({ dosageForm: v, page: 1 }); get().loadFormulas(); },
  setSchedule: (v) => { set({ schedule: v, page: 1 }); get().loadFormulas(); },
  setSearchText: (v) => { set({ searchText: v, page: 1 }); get().loadFormulas(); },
  setSearchMode: (v) => { set({ searchMode: v, page: 1 }); get().loadFormulas(); },
  setSortBy: (v) => { set({ sortBy: v, page: 1 }); get().loadFormulas(); },
  clearFilters: () => {
    set({
      status: 'unassigned',
      dosageForm: undefined,
      schedule: undefined,
      searchText: undefined,
      searchMode: 'both',
      sortBy: 'default',
      page: 1,
    });
    get().loadFormulas();
  },
  toggleSelect: (setId) => {
    set((state) => {
      const next = new Set(state.selectedSetIds);
      if (next.has(setId)) next.delete(setId); else next.add(setId);
      return { selectedSetIds: next };
    });
  },
  selectAllPage: () => {
    set((state) => {
      const next = new Set(state.selectedSetIds);
      for (const f of state.formulas) next.add(f.setId);
      return { selectedSetIds: next };
    });
  },
  deselectPage: () => {
    set((state) => {
      const next = new Set(state.selectedSetIds);
      for (const f of state.formulas) next.delete(f.setId);
      return { selectedSetIds: next };
    });
  },
  selectAllMatchingResults: async () => {
    const state = get();
    try {
      const response = await fetchAllMatchingSetIds({
        status: state.status,
        dosageForm: state.dosageForm,
        schedule: state.schedule,
        search: state.searchText,
        searchMode: state.searchMode,
      });
      const allIds = response.data ?? [];
      set({ selectedSetIds: new Set(allIds) });
    } catch {
      // Fall back silently — page selection remains
    }
  },
  clearSelection: () => set({ selectedSetIds: new Set() }),
  toggleExpanded: (setId) => {
    set((state) => {
      const next = new Set(state.expandedSetIds);
      if (next.has(setId)) next.delete(setId); else next.add(setId);
      return { expandedSetIds: next };
    });
  },
  setClaimedSetIds: (ids) => { set({ claimedSetIds: ids, page: 1 }); get().loadFormulas(); },
}));
